import React, { FC, useEffect, useState } from 'react';
import { GreenButton } from '~Styles';
import { TroubleShootButtons } from '../AddedPrinter';
import StepsComponent, { TroubleShootPageContent } from '../Components/StepsComponent';
import ContactSupport from '../Components/ContactSupport';

import starLightPowerImage from '../../Images/star-power-light.png';
import phoneToPower from '../../Images/phone-to-power.png';
import lightPowerImage from '../../Images/star-power-light.png';
import starUsb from '../../Images/star-usb.png';
import ipadCharging from '../../Images/charging.png';
import bluetoothSettings from '../../Images/bluetoothsettings.png';
import bluetoothLight from '../../Images/bluetooth-light.png';
import reset from '../../Images/reset.png';
import starEthernetOff from '../../Images/star-ethernet-off.gif';
import starEthernetOn from '../../Images/star-ethernet-on.gif';
import starEthernetBlinking from '../../Images/star-ethernet-blinking.gif';
import { useButton, useDeviceInfo, useI18n, useLogger, useSelector } from '~Hooks';

interface Props {
  retryScan: () => void;
  sessionId: string;
}

type STEPS =
  | 'CONNECTED_QUESTION'
  | 'CHARGING'
  | 'LIGHTS_QUESTION_USB'
  | 'LIGHTS_QUESTION_LAN'
  | 'LIGHTS_QUESTION_BLUETOOTH'
  | 'CHECK_POWER_CABLE'
  | 'TEST_POWER'
  | 'CHECK_USB_OUTLET'
  | 'TEST_OTHER_CABLE'
  | 'TEST_OTHER_CABLE_LAN'
  | 'RESTART'
  | 'LAN_LIGHTS_QUESTION'
  | 'ETHERNET_OFF'
  | 'BLUETOOTH_SETTINGS'
  | 'NOT_WORKING'
  | 'RESTART_APP'
  | 'RESET'
  | 'POWER_CABLE'
  | 'DEVICE_DISTANCE'
  | 'RESTART_ROUTER'
  | 'ETHERNET_ON'
  | 'ETHERNET_BLINKING'
  | 'ETHERNET_BLINKING_NEXT'
  | 'RETRY_SEARCH'
  | 'CONTACT_SUPPORT'
  | 'CHECK_COVER';

const GeneralStarGuide: FC<Props> = ({ retryScan, sessionId }) => {
  const logger = useLogger('printer-troubleshooting');
  const deviceInfo = useDeviceInfo();
  const { i18n } = useI18n();
  const [currentStep, setCurrentStep] = useState<STEPS>('CONNECTED_QUESTION');

  useEffect(() => {
    logger.info('Troubleshooting step: ' + currentStep, {
      sessionId,
    });
  }, [currentStep]);

  const isIpad = deviceInfo?.model.includes('iPad');

  const RetryScan = useButton({
    onClick: () => {
      logger.info('Scanning for printers again');
      retryScan();
    },
  });

  const RetryScanButton = (
    <TroubleShootButtons>
      <GreenButton {...RetryScan}>{i18n('Printer.Troubleshooting.RetryScan')}</GreenButton>
    </TroubleShootButtons>
  );

  const steps: {
    [key in STEPS]: TroubleShootPageContent<STEPS>;
  } = {
    CONNECTED_QUESTION: {
      title: i18n('Printer.Troubleshooting.Connection'),
      buttons: [
        {
          text: 'USB',
          next: 'CHARGING',
        },
        {
          text: 'LAN',
          next: 'LIGHTS_QUESTION_LAN',
        },
        {
          text: 'Bluetooth',
          next: 'LIGHTS_QUESTION_BLUETOOTH',
        },
      ],
    },
    CHARGING: {
      title: i18n('Printer.TS.USB.IpadCharging'),
      text: i18n('Printer.TS.USB.IpadCharging.Desc'),
      images: [
        {
          src: ipadCharging,
          height: 150,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'LIGHTS_QUESTION_USB',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'RESTART',
        },
      ],
    },
    LIGHTS_QUESTION_USB: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: lightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'CHECK_USB_OUTLET',
        },
      ],
    },
    LIGHTS_QUESTION_LAN: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: starLightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'LAN_LIGHTS_QUESTION',
        },
      ],
    },
    LIGHTS_QUESTION_BLUETOOTH: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: lightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'BLUETOOTH_SETTINGS',
        },
      ],
    },
    CHECK_USB_OUTLET: {
      title: i18n('Printer.TS.USB.CableOutlet.Question'),
      images: [
        {
          src: starUsb,
          height: 300,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_OTHER_CABLE',
        },
      ],
    },
    TEST_OTHER_CABLE: {
      title: i18n('Printer.TS.USB.TryOtherCable'),
      text: i18n('Printer.TS.USB.TryOtherCable.Desc'),
      images: [
        {
          src: starUsb,
          height: 300,
        },
      ],
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    RESTART: {
      title: i18n('Printer.TS.USB.RestartPrinter'),
      text: i18n('Printer.TS.USB.RestartPrinter.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    CHECK_COVER: {
      title: i18n('Printer.TS.CheckCover'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Done'),
          next: 'LAN_LIGHTS_QUESTION',
        },
      ],
    },
    LAN_LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LAN.LightStatus.Question'),
      images: [
        {
          src: starEthernetOff,
          height: 100,
        },
        {
          src: starEthernetOn,
          height: 100,
        },
        {
          src: starEthernetBlinking,
          height: 100,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.TS.LAN.LightStatus.Off'),
          next: 'ETHERNET_OFF',
        },
        {
          text: i18n('Printer.TS.LAN.LightStatus.On'),
          next: 'ETHERNET_ON',
        },
        {
          text: i18n('Printer.TS.LAN.LightStatus.Blinking'),
          next: 'ETHERNET_BLINKING',
        },
      ],
    },
    ETHERNET_OFF: {
      title: i18n('Printer.TS.LAN.CableConnnected.Question'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_OTHER_CABLE_LAN',
        },
      ],
    },
    ETHERNET_ON: {
      title: i18n('Printer.TS.LAN.SameNetwork.Question', isIpad),
      text: i18n('Printer.TS.LAN.SameNetwork.Desc', isIpad),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    ETHERNET_BLINKING: {
      title: i18n('Printer.TS.LAN.CheckCables'),
      text: i18n('Printer.TS.LAN.CheckCables.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Done'),
          next: 'ETHERNET_BLINKING_NEXT',
        },
      ],
    },
    ETHERNET_BLINKING_NEXT: {
      title: i18n('Printer.TS.LAN.CheckLights'),
      text: i18n('Printer.TS.LAN.CheckLights.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESTART_ROUTER',
        },
      ],
    },
    TEST_OTHER_CABLE_LAN: {
      title: i18n('Printer.TS.LAN.TryOtherCable'),
      text: i18n('Printer.TS.LAN.TryOtherCable.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESTART_ROUTER',
        },
      ],
    },
    RESTART_ROUTER: {
      title: i18n('Printer.TS.LAN.RestartRouter'),
      text: i18n('Printer.TS.LAN.RestartRouter.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    BLUETOOTH_SETTINGS: {
      title: i18n('Printer.TS.BT.GoToSettings'),
      text: i18n('Printer.TS.BT.GoToSettings.Desc'),
      images: [
        {
          src: bluetoothSettings,
        },
      ],
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'NOT_WORKING',
        },
      ],
    },
    NOT_WORKING: {
      title: i18n('Printer.TS.BT.LightsOn'),
      images: [
        {
          src: bluetoothLight,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'RESTART_APP',
        },
      ],
    },
    RESTART_APP: {
      title: i18n('Printer.TS.BT.Restart'),
      text: i18n('Printer.TS.BT.Restart.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESET',
        },
      ],
    },
    RESET: {
      title: i18n('Printer.TS.BT.ResetPrinter'),
      text: i18n('Printer.TS.BT.ResetPrinter.Desc'),
      images: [
        {
          src: reset,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Next'),
          next: 'BLUETOOTH_SETTINGS',
        },
      ],
    },
    POWER_CABLE: {
      title: i18n('Printer.TS.BT.PullOutCable'),
      text: i18n('Printer.TS.BT.PullOutCable.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'DEVICE_DISTANCE',
        },
      ],
    },
    DEVICE_DISTANCE: {
      title: i18n('Printer.TS.BT.CheckDistance'),
      text: i18n('Printer.TS.BT.CheckDistance.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    CHECK_POWER_CABLE: {
      title: i18n('Printer.TS.CablePluggedIn'),
      text: i18n('Printer.TS.CablePluggedIn.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'TEST_POWER',
        },
      ],
    },
    TEST_POWER: {
      title: i18n('Printer.TS.PowerWorking'),
      text: i18n('Printer.TS.PowerWorking.Desc'),
      images: [
        {
          src: phoneToPower,
          height: 250,
        },
      ],
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    RETRY_SEARCH: {
      title: i18n('Printer.TS.LAN.RetryScan'),
      text: i18n('Printer.TS.LAN.RetryScan.Desc'),
      custom: <>{RetryScanButton}</>,
    },
    CONTACT_SUPPORT: {
      custom: <ContactSupport />,
    },
  };

  return (
    <>
      <StepsComponent<STEPS>
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
    </>
  );
};

export default GeneralStarGuide;
